import { z } from 'zod';
/**
 * Área do médico, etapa M1 (rodada 15): o prontuário. Cada atendimento é um registro com
 * autor e hora; enquanto é rascunho, salva sozinho; depois de assinado não muda mais
 * (CFM 1.821/2007, Lei 13.787/2018): corrigir é um aditamento, com autor e hora próprios.
 * Só quem tem a permissão do dado clínico vê e escreve.
 */
const isoDateTime = z.iso.datetime();
export const CLINIC_RECORD_BODY_MAX = 20_000;
export const CLINIC_RECORD_KINDS = ['evolucao', 'aditamento'];
export const CLINIC_RECORD_STATUSES = ['rascunho', 'assinado'];
/** Código CID-10 (A00 a Z99, com subcategoria opcional). */
export const CID_CODE = /^[A-Z]\d{2}(\.\d{1,2})?$/;
export const clinicCidSchema = z.object({
    code: z.string().trim().toUpperCase().regex(CID_CODE, 'Código CID no formato A00 ou A00.0'),
    label: z.string().trim().max(120),
});
export const clinicRecordSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    appointmentId: z.uuid().nullable(),
    kind: z.enum(CLINIC_RECORD_KINDS),
    /** Aditamento: o registro que ele corrige ou completa. */
    amendsId: z.uuid().nullable(),
    status: z.enum(CLINIC_RECORD_STATUSES),
    authorId: z.uuid(),
    author: z.string(),
    templateKey: z.string().nullable(),
    body: z.string(),
    hypothesis: z.string(),
    cids: z.array(clinicCidSchema),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
    signedAt: isoDateTime.nullable(),
});
/** O prontuário de uma pessoa: os assinados (com os aditamentos) e o rascunho de quem pede. */
export const clinicRecordListSchema = z.object({
    records: z.array(clinicRecordSchema),
    draft: clinicRecordSchema.nullable(),
});
export const openClinicRecordSchema = z.object({
    appointmentId: z.uuid().optional(),
    templateKey: z.string().trim().max(40).optional(),
});
export const saveClinicRecordSchema = z.object({
    body: z.string().max(CLINIC_RECORD_BODY_MAX).optional(),
    hypothesis: z.string().trim().max(400).optional(),
    cids: z.array(clinicCidSchema).max(8).optional(),
    templateKey: z.string().trim().max(40).nullable().optional(),
});
/** Medidas do atendimento: entram nos resultados da pessoa (curvas), junto com a assinatura. */
export const clinicVisitMeasuresSchema = z.object({
    weightKg: z.number().min(10).max(400).optional(),
    heightCm: z.number().min(80).max(250).optional(),
    waistCm: z.number().min(30).max(250).optional(),
    hipCm: z.number().min(30).max(250).optional(),
    systolic: z.number().int().min(60).max(260).optional(),
    diastolic: z.number().int().min(30).max(160).optional(),
    heartRate: z.number().int().min(25).max(220).optional(),
});
export const signClinicRecordSchema = z.object({
    measures: clinicVisitMeasuresSchema.optional(),
});
export const amendClinicRecordSchema = z.object({
    body: z.string().trim().min(2).max(CLINIC_RECORD_BODY_MAX),
});
/** O que a equipe clínica precisa saber antes de chamar: alergias, remédios em uso, condições. */
export const clinicClinicalSchema = z.object({
    allergies: z.string().max(1000),
    medications: z.string().max(2000),
    conditions: z.string().max(2000),
    updatedAt: isoDateTime.nullable(),
    updatedBy: z.string().nullable(),
});
export const updateClinicClinicalSchema = z
    .object({
    allergies: z.string().trim().max(1000),
    medications: z.string().trim().max(2000),
    conditions: z.string().trim().max(2000),
})
    .partial();
/** "Paciente de hoje": o resumo de antes da consulta, numa tela só. */
export const clinicPatientTodaySchema = z.object({
    clinical: clinicClinicalSchema,
    /** Remédios entregues pela clínica nos últimos 120 dias (nome e quantidade). */
    delivered: z.array(z.object({ name: z.string(), quantity: z.string(), at: isoDateTime })),
    lastRecord: clinicRecordSchema.nullable(),
    /** Peso nas últimas medições, do mais antigo para o mais novo. */
    weights: z.array(z.object({ at: isoDateTime, kg: z.number() })),
    /** Última pressão e frequência medidas. */
    vitals: z
        .object({
        at: isoDateTime,
        systolic: z.number().nullable(),
        diastolic: z.number().nullable(),
        heartRate: z.number().nullable(),
    })
        .nullable(),
    visits: z.object({ attended: z.number().int().min(0), missed: z.number().int().min(0) }),
});
/** Modelo de evolução: os de fábrica por especialidade e os que a clínica escreveu. */
export const clinicRecordTemplateSchema = z.object({
    key: z.string(),
    title: z.string(),
    body: z.string(),
    custom: z.boolean(),
});
export const saveClinicRecordTemplateSchema = z.object({
    title: z.string().trim().min(2).max(60),
    body: z.string().trim().min(2).max(6000),
});
/** Modelos de fábrica: o roteiro que o profissional preenche (e pode trocar). */
export const CLINIC_RECORD_TEMPLATES = [
    {
        key: 'consulta',
        title: 'Primeira consulta',
        for: ['medico'],
        body: 'Queixa principal:\n\nHistória da doença atual:\n\nAntecedentes (pessoais e familiares):\n\nHábitos (sono, álcool, tabaco, atividade física):\n\nExame físico:\n\nAvaliação:\n\nConduta:\n',
    },
    {
        key: 'retorno',
        title: 'Retorno',
        for: ['medico', 'fisioterapia', 'estetica', 'outro'],
        body: 'Desde a última consulta:\n\nAdesão ao tratamento:\n\nEfeitos e queixas:\n\nExame:\n\nConduta:\n',
    },
    {
        key: 'emagrecimento',
        title: 'Acompanhamento de emagrecimento',
        for: ['emagrecimento'],
        body: 'Peso e medidas (anotadas abaixo):\n\nAlimentação na semana:\n\nAtividade física:\n\nMedicação e dose atual:\n\nEfeitos colaterais:\n\nConduta e próxima dose:\n',
    },
    {
        key: 'hormonal',
        title: 'Reposição hormonal',
        for: ['hormonal', 'saude_sexual'],
        body: 'Sintomas desde o último retorno:\n\nExames (data e resultados):\n\nDose e via atuais:\n\nEfeitos e eventos adversos:\n\nConduta:\n',
    },
    // Nutrição: os campos da primeira consulta, do retorno e do encerramento (Res. CFN 594/2017).
    {
        key: 'nutricao',
        title: 'Primeira consulta de nutrição',
        for: ['nutricao'],
        body: 'Motivo e objetivo:\n\nHistória clínica e exames:\n\nHistória alimentar e recordatório:\n\nPreferências, aversões e intolerâncias:\n\nSuplementos em uso:\n\nAtividade, sono, água e intestino:\n\nAntropometria (anotada abaixo):\n\nDiagnóstico nutricional:\n\nPrescrição e orientações:\n\nMetas até o retorno:\n',
    },
    {
        key: 'nutricao_retorno',
        title: 'Retorno de nutrição',
        for: ['nutricao'],
        body: 'Desde a última consulta:\n\nAdesão ao plano:\n\nAntropometria (anotada abaixo):\n\nDiagnóstico nutricional atual:\n\nAjustes na prescrição:\n\nMetas até o retorno:\n',
    },
    {
        key: 'nutricao_encerramento',
        title: 'Alta ou abandono',
        for: ['nutricao'],
        body: 'Motivo (alta ou abandono):\n\nResultado em relação às metas:\n\nOrientações finais:\n',
    },
    {
        key: 'treino',
        title: 'Avaliação física',
        for: ['educacao_fisica', 'treino'],
        body: 'Objetivo:\n\nHistórico de lesões e restrições:\n\nTestes:\n\nAvaliação:\n\nPlano de treino:\n',
    },
    {
        key: 'odontologia',
        title: 'Atendimento odontológico',
        for: ['odontologia'],
        body: 'Queixa:\n\nExame clínico:\n\nProcedimento realizado (dentes e faces):\n\nOrientações:\n\nPróxima etapa:\n',
    },
    {
        key: 'sessao',
        title: 'Sessão',
        for: ['psicologia', 'psicoterapia'],
        body: 'Tema da sessão:\n\nObservações:\n\nIntervenções:\n\nCombinados para a próxima:\n',
    },
    {
        key: 'procedimento',
        title: 'Procedimento',
        for: ['estetica', 'fisioterapia', 'reabilitacao'],
        body: 'Área e técnica:\n\nProdutos e parâmetros:\n\nIntercorrências:\n\nOrientações:\n\nRetorno:\n',
    },
];
/** Os modelos de fábrica que servem a uma clínica (pela especialidade e pelos serviços). */
export function recordTemplatesFor(specialty, services) {
    const tags = new Set([specialty, ...services]);
    const list = CLINIC_RECORD_TEMPLATES.filter((row) => row.for.some((tag) => tags.has(tag)));
    return list.length > 0 ? list : CLINIC_RECORD_TEMPLATES.filter((row) => row.key === 'retorno');
}
/** CID-10 mais usados nos serviços do Sec (o profissional digita qualquer outro código). */
export const CLINIC_COMMON_CIDS = [
    { code: 'E66.0', label: 'Obesidade devida a excesso de calorias' },
    { code: 'E66.9', label: 'Obesidade não especificada' },
    { code: 'R63.5', label: 'Ganho de peso anormal' },
    { code: 'R63.4', label: 'Perda de peso anormal' },
    { code: 'E11.9', label: 'Diabetes tipo 2 sem complicações' },
    { code: 'E10.9', label: 'Diabetes tipo 1 sem complicações' },
    { code: 'R73.0', label: 'Teste de tolerância à glicose anormal' },
    { code: 'E16.2', label: 'Hipoglicemia não especificada' },
    { code: 'E78.0', label: 'Hipercolesterolemia pura' },
    { code: 'E78.1', label: 'Hipergliceridemia pura' },
    { code: 'E78.5', label: 'Hiperlipidemia não especificada' },
    { code: 'I10', label: 'Hipertensão essencial' },
    { code: 'K76.0', label: 'Degeneração gordurosa do fígado' },
    { code: 'G47.3', label: 'Apneia de sono' },
    { code: 'E29.1', label: 'Hipofunção testicular' },
    { code: 'N48.4', label: 'Impotência de origem orgânica' },
    { code: 'F52.0', label: 'Ausência ou perda do desejo sexual' },
    { code: 'N46', label: 'Infertilidade masculina' },
    { code: 'N97.9', label: 'Infertilidade feminina não especificada' },
    { code: 'E28.2', label: 'Síndrome do ovário policístico' },
    { code: 'N95.1', label: 'Menopausa e climatério feminino' },
    { code: 'E03.9', label: 'Hipotireoidismo não especificado' },
    { code: 'E05.9', label: 'Tireotoxicose não especificada' },
    { code: 'E55.9', label: 'Deficiência de vitamina D' },
    { code: 'D50.9', label: 'Anemia por deficiência de ferro' },
    { code: 'R53', label: 'Mal-estar e fadiga' },
    { code: 'K21.9', label: 'Refluxo gastroesofágico sem esofagite' },
    { code: 'F32.9', label: 'Episódio depressivo não especificado' },
    { code: 'F41.1', label: 'Ansiedade generalizada' },
    { code: 'F41.9', label: 'Transtorno ansioso não especificado' },
    { code: 'G47.0', label: 'Distúrbio do início e da manutenção do sono' },
    { code: 'L70.0', label: 'Acne vulgar' },
    { code: 'L64.9', label: 'Alopécia androgênica' },
    { code: 'L65.9', label: 'Perda de cabelos não cicatricial' },
    { code: 'M54.5', label: 'Dor lombar baixa' },
    { code: 'M54.2', label: 'Cervicalgia' },
    { code: 'M25.5', label: 'Dor articular' },
    { code: 'M79.1', label: 'Mialgia' },
    { code: 'K02.9', label: 'Cárie dentária não especificada' },
    { code: 'K05.1', label: 'Gengivite crônica' },
    { code: 'K05.3', label: 'Periodontite crônica' },
    { code: 'Z00.0', label: 'Exame médico geral' },
    { code: 'Z71.3', label: 'Aconselhamento e supervisão dietéticos' },
    { code: 'Z30.0', label: 'Aconselhamento sobre contracepção' },
];
